import React from 'react';
import {
    ChevronUp,
    ChevronDown,
    ChevronsLeft,
    ChevronsRight,
    ChevronLeft,
    ChevronRight,
} from 'lucide-react';
import { AraTableRow } from '../api';
import { useSmoothLoading } from 'lib/use-smooth-loading';
import { Skeleton } from 'components/ui/skeleton';

interface Props {
    data: AraTableRow[];
    loading: boolean;
    page: number;
    pageSize: number;
    totalItems: number;
    sortBy: keyof AraTableRow;
    sortOrder: 'asc' | 'desc';
    onSortChange: (column: keyof AraTableRow) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    hasLoadedOnce: boolean;
    hasFiltersSelected: boolean;
    error?: string;
    conversionType: 'INR' | 'Lakhs' | 'Crores';
    onConversionChange: (type: 'INR' | 'Lakhs' | 'Crores') => void;
    onCustomerClick: (customerName: string) => void;
}

const columns: Array<{ key: keyof AraTableRow; label: string; isNumeric: boolean }> = [
    { key: 'Customer', label: 'Customer', isNumeric: false },
    { key: 'CreditPeriod', label: 'Credit Period', isNumeric: false },
    { key: 'TotalOutstanding', label: 'Total Outstanding', isNumeric: true },
    { key: 'NotDue', label: 'Not Due', isNumeric: true },
    { key: 'TotalOverdue', label: 'Total Overdue', isNumeric: true },
    { key: 'Bucket1_30', label: '1–30', isNumeric: true },
    { key: 'Bucket31_60', label: '31–60', isNumeric: true },
    { key: 'Bucket61_90', label: '61–90', isNumeric: true },
    { key: 'Bucket91_120', label: '91–120', isNumeric: true },
    { key: 'Bucket121_180', label: '121–180', isNumeric: true },
    { key: 'BucketOver180', label: '>180', isNumeric: true },
    { key: 'Legal', label: 'Legal', isNumeric: true },
    { key: 'UnappliedReceipts', label: 'Unapplied Receipts', isNumeric: true },
    { key: 'NetOutstanding', label: 'Net Outstanding', isNumeric: true },
];

const AraTable: React.FC<Props> = ({
    data,
    loading,
    page,
    pageSize,
    totalItems,
    sortBy,
    sortOrder,
    onSortChange,
    onPageChange,
    onPageSizeChange,
    hasLoadedOnce,
    hasFiltersSelected,
    error,
    conversionType,
    onConversionChange,
    onCustomerClick,
}) => {
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const { isLoading, showContent } = useSmoothLoading(loading, {
        minLoadingTime: 150,
        debounceTime: 150,
    });

    const formatNumber = (value: number): string => {
        let displayValue = value;
        switch (conversionType) {
            case 'Lakhs':
                displayValue = value / 100000;
                break;
            case 'Crores':
                displayValue = value / 10000000;
                break;
            case 'INR':
            default:
                displayValue = value;
                break;
        }

        return displayValue.toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const renderLoadingRows = () => {
        const rows = 15;
        const cols = columns.length;

        return Array.from({ length: rows }).map((_, rowIndex) => (
            <tr key={`skeleton-row-${rowIndex}`} className="hover:bg-gray-50">
                {Array.from({ length: cols }).map((_, colIndex) => (
                    <td
                        key={`skeleton-cell-${rowIndex}-${colIndex}`}
                        className="border border-gray-200 px-2 py-1.5"
                    >
                        <Skeleton
                            className={`h-4 ${colIndex === 0 ? 'w-3/4' : 'w-full'}`}
                        />
                    </td>
                ))}
            </tr>
        ));
    };

    const opacityClass = isLoading || showContent ? 'opacity-100' : 'opacity-0';

    return (
        <div className={`flex flex-col h-full min-h-0 transition-opacity duration-300 ${opacityClass}`}>
            {/* Controls Row */}
            <div className="mb-4 flex gap-2">
                {(['INR', 'Lakhs', 'Crores'] as const).map((t) => (
                    <button
                        key={t}
                        onClick={() => onConversionChange(t)}
                        className={`px-3 py-1 text-sm rounded-md transition-colors ${conversionType === t
                            ? 'bg-blue-600 text-white'
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        }`}
                    >
                        {t}
                    </button>
                ))}
            </div>

            {/* Table Container */}
            <div className="w-full max-h-full overflow-auto border border-gray-200">
                <table className="w-full border-collapse text-sm">
                    <thead>
                        <tr className="bg-gray-100">
                            {columns.map((col) => {
                                const isActive = sortBy === col.key;
                                return (
                                    <th
                                        key={col.key}
                                        onClick={() => onSortChange(col.key)}
                                        className={`border border-gray-300 px-2 py-2 ${col.isNumeric ? 'text-center' : 'text-left'} text-gray-700 font-bold whitespace-nowrap cursor-pointer`}
                                    >
                                        <div className="flex items-center justify-center gap-1">
                                            {col.label}
                                            {isActive ? (
                                                sortOrder === 'asc' ? (
                                                    <ChevronUp size={14} />
                                                ) : (
                                                    <ChevronDown size={14} />
                                                )
                                            ) : (
                                                <ChevronUp size={14} className="text-gray-300" />
                                            )}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            renderLoadingRows()
                        ) : !hasFiltersSelected ? (
                            <tr>
                                <td colSpan={columns.length} className="text-center py-20 text-gray-500">
                                    Please select filters to fetch data.
                                </td>
                            </tr>
                        ) : error ? (
                            <tr>
                                <td colSpan={columns.length} className="text-center py-20 text-red-600">
                                    {error}
                                </td>
                            </tr>
                        ) : data.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className="text-center py-20 text-gray-500">
                                    No data available for the selected filters.
                                </td>
                            </tr>
                        ) : (
                            data.map((row, idx) => (
                                <tr key={idx} onClick={() => idx !== 0 && onCustomerClick(row.Customer)} className={`hover:bg-gray-50 ${idx !== 0 ? 'cursor-pointer' : ''} ${idx === 0 ? 'bg-blue-50 font-semibold' : ''}`}>
                                    {columns.map((col) => (
                                        <td
                                            key={col.key}
                                            className={`border border-gray-200 px-2 py-1.5 whitespace-nowrap ${col.isNumeric ? 'text-right' : 'text-left'
                                                }`}
                                        >
                                            {col.isNumeric && typeof row[col.key] === 'number'
                                                ? formatNumber(row[col.key] as number)
                                                : col.key === 'CreditPeriod' && idx === 0 && !row[col.key]
                                                    ? '-'
                                                    : row[col.key]
                                            }
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Controls */}
            {hasFiltersSelected && !error && (
                <div className="flex items-center justify-between shrink-0 border-t border-gray-200 bg-white pt-4">
                    <span className="text-sm text-gray-600">
                        Showing{' '}
                        <span className="font-semibold">{totalItems === 0 ? 0 : (page - 1) * pageSize + 1}</span>{' '}
                        to{' '}
                        <span className="font-semibold">{Math.min(page * pageSize, totalItems)}</span> of{' '}
                        <span className="font-semibold">{totalItems}</span> results
                    </span>

                    <div className="flex items-center gap-3 text-sm space-x-10">
                        <div className="flex items-center gap-1">
                            <span className="text-gray-600 mr-1">Rows per page</span>
                            <select
                                value={pageSize}
                                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                                className="border border-gray-300 rounded px-2 py-1"
                            >
                                {[10, 20, 50, 100].map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <span className="text-gray-600">
                            Page <strong>{page}</strong> of <strong>{totalPages}</strong>
                        </span>

                        <div className="flex items-center gap-1">
                            <button
                                disabled={page === 1}
                                onClick={() => onPageChange(1)}
                                className="p-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
                            >
                                <ChevronsLeft size={16} />
                            </button>

                            <button
                                disabled={page === 1}
                                onClick={() => onPageChange(page - 1)}
                                className="p-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
                            >
                                <ChevronLeft size={16} />
                            </button>

                            <button
                                disabled={page === totalPages}
                                onClick={() => onPageChange(page + 1)}
                                className="p-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
                            >
                                <ChevronRight size={16} />
                            </button>

                            <button
                                disabled={page === totalPages}
                                onClick={() => onPageChange(totalPages)}
                                className="p-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
                            >
                                <ChevronsRight size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AraTable;
